import type { Doc } from "building-types/doc";
import type {
  ComponentAssociatedEntityHandle,
  ComponentHandle,
  ComponentMemberEntityHandle,
  DocEntityHandle,
  GroupableEntityHandle,
  GroupHandle,
  RoomLayoutHandle,
} from "building-types/handles/docEntityHandleTypes";
import {
  componentAssociatedEntityTypes,
  isComponentOwnedMemberEntityHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { RoomLayoutEntityHandle } from "building-types/roomLayouts/roomLayoutTypes";
import type { RWTable, Table } from "common/data/table";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import { SystemEntityGroups } from "../docSystemGroups";
import { docGetAssociatedEntityIdsForComponent } from "../options/componentAssociations";
import { isRoomLayoutEntityHandle } from "../rooms/layout/roomLayoutTools";
import { isGroupableEntityHandle } from "./docEntityGroupable";
import { docGetEntityHandleByHash, isDocEntityHandleHash } from "./docEntityHandleHash";

interface HashTableEntitiesCacheKey {
  doc: Doc;
  hashTable: Table<string>;
}

function docBuildHashTableEntities(doc: Doc, hashTable: Table<string>): Table<DocEntityHandle[]> {
  const entries: RWTable<DocEntityHandle[]> = {};

  for (const [key, value] of Object.entries(hashTable)) {
    if (!isDocEntityHandleHash(key)) {
      console.warn(`Invalid hash table key: ${key}`);
      continue;
    }

    if (typeof value !== "string") {
      console.warn(`Invalid hash table value: ${value}`);
      continue;
    }

    const handle = docGetEntityHandleByHash(doc, key);

    if (!handle) {
      console.warn(`Invalid hash table key: ${key}`);
      continue;
    }

    if (!entries[value]) {
      entries[value] = [];
    }
    entries[value].push(handle);
  }

  return entries;
}

const hashTableEntriesCache = new LRUObjectCache<HashTableEntitiesCacheKey, Table<DocEntityHandle[]>>(
  10,
  (a, b) => a.doc === b.doc && a.hashTable === b.hashTable,
);

export function docGetHashTableEntities(doc: Doc, hashTable: Table<string>): Table<DocEntityHandle[]> {
  const cacheKey: HashTableEntitiesCacheKey = { doc, hashTable };
  const cached = hashTableEntriesCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  const entries = docBuildHashTableEntities(doc, hashTable);
  hashTableEntriesCache.add(cacheKey, entries);

  return entries;
}

export function docGetGroupEntities(doc: Doc, handle: GroupHandle): GroupableEntityHandle[] {
  const systemEntityGroups = docGetHashTableEntities(doc, SystemEntityGroups);
  if (systemEntityGroups[handle.id]) {
    return systemEntityGroups[handle.id].filter(isGroupableEntityHandle);
  }
  const entityGroups = docGetHashTableEntities(doc, doc.entityGroups);
  if (entityGroups[handle.id]) {
    return entityGroups[handle.id].filter(isGroupableEntityHandle);
  }
  return [];
}

export function docGetComponentEntities(doc: Doc, handle: ComponentHandle): ComponentMemberEntityHandle[] {
  const entries = docGetHashTableEntities(doc, doc.componentMembers);
  const entities = entries[handle.id] ?? [];
  const memberEntities = entities.filter(isComponentOwnedMemberEntityHandle);

  const associatedEntities: ComponentAssociatedEntityHandle[] = componentAssociatedEntityTypes.flatMap((entityType) =>
    docGetAssociatedEntityIdsForComponent(doc, handle.id, entityType).map(
      (entityId): ComponentAssociatedEntityHandle => ({ type: entityType, id: entityId }),
    ),
  );

  return [...memberEntities, ...associatedEntities];
}

export function docGetRoomLayoutEntities(doc: Doc, handle: RoomLayoutHandle): RoomLayoutEntityHandle[] {
  const entries = docGetHashTableEntities(doc, doc.entityRoomLayouts);
  const entities = entries[handle.id] ?? [];
  return entities.filter(isRoomLayoutEntityHandle);
}
